import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { warehousesService } from '../services/warehouses.service';
import type { Warehouse, WarehouseCreatePayload, WarehouseUpdatePayload, WarehouseFilters } from '../types/warehouses.types';
import { toast } from 'sonner';

export function useWarehouses() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<WarehouseFilters>({
    search: '',
    is_active: 'all',
    page: 1,
    pageSize: 10,
  });

  const queryKey = ['inventory', 'warehouses', companyId, filters.search, filters.is_active];

  const query = useQuery({
    queryKey,
    queryFn: () => warehousesService.fetchWarehouses(companyId, filters.search, filters.is_active),
    enabled: !!companyId,
  });

  const items = useMemo(() => {
    if (!query.data) return [];
    const start = (filters.page - 1) * filters.pageSize;
    const end = start + filters.pageSize;
    return query.data.slice(start, end);
  }, [query.data, filters.page, filters.pageSize]);

  const total = query.data?.length || 0;

  const createMutation = useMutation({
    mutationFn: (payload: WarehouseCreatePayload) => warehousesService.createWarehouse(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'warehouses', companyId] });
      toast.success('Thêm kho bãi thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi thêm kho bãi');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: WarehouseUpdatePayload }) => 
      warehousesService.updateWarehouse(userId, companyId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'warehouses', companyId] });
      toast.success('Cập nhật kho bãi thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi cập nhật kho bãi');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => warehousesService.deleteWarehouse(userId, companyId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'warehouses', companyId] });
      toast.success('Xóa kho bãi thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi xóa kho bãi. Kho bãi có thể đang được sử dụng.');
    },
  });

  return {
    data: items,
    total,
    allData: query.data || [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    filters,
    setFilters,
    updateSearch: (search: string) => setFilters((f) => ({ ...f, search, page: 1 })),
    updateStatus: (is_active: string | boolean) => setFilters((f) => ({ ...f, is_active, page: 1 })),
    updatePage: (page: number) => setFilters((f) => ({ ...f, page })),
    createWarehouse: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateWarehouse: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteWarehouse: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
